import { Component, OnInit, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { UseHttpImageSourcePipe } from '../../pipes';
import { Language, LocalStorage } from '../../enums';
import { AuthService, SessionService, UtilsService } from '../../services';
import { CONSTANTS_TOKEN } from '../../config';
import { filter, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'educare-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  imports: [UseHttpImageSourcePipe, CommonModule, TranslateModule, MatMenuModule, RouterModule]
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly sessionService = inject(SessionService);
  private readonly translate = inject(TranslateService);
  private readonly utils = inject(UtilsService);
  private readonly router = inject(Router);
  private readonly constants = inject(CONSTANTS_TOKEN);
  private readonly destroy$ = new Subject<void>();

  currentLanguage: string = Language.English;
  language = Language;
  userInfo: any = {};
  pageTitle: string = '';

  onLogout() {
    this.authService.logoutUser();
  }

  ngOnInit(): void {
    this.userInfo = this.authService.getUser()?.user;
    this.authService.user$.subscribe(() => {
      this.userInfo = this.authService.getUser()?.user;
    });
    this.switchLanguage(undefined, false);
    this.initializePageTitle();
    this.subscribeToRouteChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializePageTitle(): void {
    this.updatePageTitle();
  }

  private subscribeToRouteChanges(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updatePageTitle();
      });
  }

  private updatePageTitle(): void {
    const currentUrl = this.router.url.split('/').filter((segment) => segment);
    const navigationConfig = this.constants.shared.navigation;
    let matchedLabel = '';

    // Check if we're on the home/dashboard page (empty route, /, "home", or "dashboard" route)
    if (
      currentUrl.length === 0 ||
      this.router.url === '/' ||
      currentUrl[0] === 'home' ||
      currentUrl[0] === 'dashboard'
    ) {
      // Show "Welcome to Educare" on home page for user portal
      this.pageTitle = 'header.header';
      return;
    }

    if (currentUrl.length > 0) {
      const firstSegment = currentUrl[0];

      // Check main navigation items
      const mainNavItem = navigationConfig.find((item: any) => {
        if (item.routerLink && Array.isArray(item.routerLink)) {
          return item.routerLink[0] === firstSegment;
        }
        return false;
      });

      if (mainNavItem) {
        matchedLabel = mainNavItem.label;
      } else {
        // Check sublinks for audit trail items
        const subNavItem = navigationConfig.find((item: any) => {
          if (item.subLinks && Array.isArray(item.subLinks)) {
            return item.subLinks.some((subLink: any) => {
              const subPath = subLink.routerLink[0].split('/')[0];
              return subPath === firstSegment;
            });
          }
          return false;
        });

        if (subNavItem) {
          // Find the specific sub-item
          const specificSubItem = subNavItem.subLinks.find((subLink: any) => {
            return subLink.routerLink[0].includes(currentUrl[1]);
          });
          // Format as "Parent > Child" for audit trail items with translated labels
          if (specificSubItem) {
            const parentLabel = this.translate.instant(subNavItem.label);
            const childLabel = this.translate.instant(specificSubItem.label);
            this.pageTitle = `${parentLabel} > ${childLabel}`;
            return;
          } else {
            matchedLabel = subNavItem.label;
          }
        }
      }
    }

    // Update page title with the matched label or fallback
    this.pageTitle = matchedLabel || 'header.header';
  }

  switchLanguage(language?: string, callApi: boolean = true) {
    if (language !== this.currentLanguage) {
      this.currentLanguage = language ?? this.sessionService.getItem(LocalStorage.CurrentLanguage) ?? Language.English;
      this.translate.use(this.currentLanguage);
      this.sessionService.setItem(LocalStorage.CurrentLanguage, this.currentLanguage);
      this.toggleLanguage();
      if (callApi) {
        this.authService
          .post(this.utils.getEncryptedUrls(this.constants.api.privateEndpoints.updateLanguage), {
            language: this.currentLanguage
          })
          .subscribe({
            next: () => {
              this.utils.locationReload();
            }
          });
      }
    }
  }

  toggleLanguage() {
    if (this.currentLanguage === Language.Arabic) {
      document.body.classList.add('arabic-translation-div');
    } else {
      document.body.classList.remove('arabic-translation-div');
    }
  }

  navigateToProfile() {
    if (this.constants.shared.header.isProfileClickable && this.router.url !== '/face-verification') {
      this.router.navigate(['profile-settings']);
    }
  }
}
